import { DateTime } from 'luxon';

import {
  CheckMetadata,
  CheckMetadataItem,
  CheckMetadataItemType,
  renderCheckNameCell,
  renderMatchResultCell,
  renderStatusCell,
} from 'dashboard/components/ChecksTable/computeCheckMetadata';
import Pii from 'dashboard/components/Pii';
import { formatDate } from 'dashboard/lib/utils/formatters';
import {
  Check,
  CheckAttributeMatch,
  CheckName,
  DatabaseAadhaarBirthdateComparison,
  DatabaseAadhaarNameComparison,
} from 'dashboard/models/check';
import { VerificationDatabaseAadhaar } from 'dashboard/models/verification';
import { CHECK_DISPLAY_CONFIG } from 'lib/constants/check';
import { sortChecks } from 'lib/utils/checks';

const ATTRIBUTE_CHECK_NAMES = [
  CheckName.DatabaseAadhaarBirthdateComparison,
  CheckName.DatabaseAadhaarNameComparison,
];

export const computeDatabaseAadhaarAttributeChecksMetadata = (
  verification: VerificationDatabaseAadhaar,
): CheckMetadata => {
  const sortedChecks = sortChecks(verification.attributes?.checks || []);
  const isRedacted = verification.attributes?.['redacted-at'] != null;

  const rows = sortedChecks
    .filter((check): check is DatabaseAadhaarBirthdateComparison | DatabaseAadhaarNameComparison =>
      ATTRIBUTE_CHECK_NAMES.includes(check.name),
    )
    .map((check) => {
      const checkDetails = CHECK_DISPLAY_CONFIG[check.name];
      if (checkDetails == null) {
        return null;
      }

      const metadata = check.metadata;
      if (
        metadata == null ||
        metadata['match-level-minimum'] == null ||
        metadata['match-result'] == null
      ) {
        return null;
      }

      const matchResult = metadata['match-result'] as CheckAttributeMatch;

      return [
        renderCheckNameCell(checkDetails.name, checkDetails.description),
        renderInputCell(check, verification, isRedacted),
        renderMatchResultCell(matchResult, undefined, metadata['match-level-minimum']),
        renderStatusCell(check.status),
      ];
    })
    .filter(Boolean);

  return {
    metadata: [
      {
        type: CheckMetadataItemType.Table,
        heading: {
          title: 'Comparison Check',
        },
        rowHeaders: ['Property', 'Input', 'Match Result', 'Status'],
        rows,
        tableEmptyState: 'No comparison checks were performed for this verification.',
      },
    ] as CheckMetadataItem[],
  };
};

function renderInputCell(
  check: Check,
  verification: VerificationDatabaseAadhaar,
  isRedacted: boolean,
) {
  const { 'name-first': nameFirst, 'name-last': nameLast, birthdate } = verification.attributes;
  const nameFull = [nameFirst, nameLast].filter(Boolean).join(' ');

  switch (check.name) {
    case CheckName.DatabaseAadhaarNameComparison:
      return <Pii isRedacted={isRedacted}>{nameFull}</Pii>;
    case CheckName.DatabaseAadhaarBirthdateComparison:
      return (
        <Pii isRedacted={isRedacted}>
          <span>
            {birthdate &&
              `${formatDate(birthdate)} (${Math.floor(
                DateTime.local().diff(DateTime.fromISO(birthdate), 'years').years,
              )} years old)`}
          </span>
        </Pii>
      );
      break;
  }
}
